<template>
  <main class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 text-white overflow-hidden">
      <div class="absolute inset-0 bg-black opacity-20"></div>
      <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
      
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-10">
        <svg class="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" stroke-width="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#grid)" />
        </svg>
      </div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div class="text-center">
          <h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Scholarship <span class="text-yellow-400">Program</span>
          </h1>
          <p class="text-xl sm:text-2xl mb-8 text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Empowering brilliant minds to achieve their educational dreams through financial support and mentorship
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              @click="scrollToSection('application')"
              class="bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-yellow-300 transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Apply Now
            </button>
            <button 
              @click="scrollToSection('about')"
              class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transform hover:scale-105 transition-all duration-300"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Quick Stats -->
    <section class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          <div class="p-6">
            <div class="text-4xl font-bold text-blue-600 mb-2">500+</div>
            <div class="text-gray-600 text-lg">Students Supported</div>
          </div>
          <div class="p-6">
            <div class="text-4xl font-bold text-purple-600 mb-2">₦50M+</div>
            <div class="text-gray-600 text-lg">Funds Disbursed</div>
          </div>
          <div class="p-6">
            <div class="text-4xl font-bold text-indigo-600 mb-2">95%</div>
            <div class="text-gray-600 text-lg">Graduation Rate</div>
          </div>
          <div class="p-6">
            <div class="text-4xl font-bold text-yellow-600 mb-2">50+</div>
            <div class="text-gray-600 text-lg">Universities Partnered</div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Scholarship Program -->
    <section id="about" class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            About Our Scholarship Program
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            The Mshel Foundation Scholarship Program is designed to break financial barriers and create pathways to higher education for deserving students across Nigeria.
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 class="text-2xl font-bold text-gray-900 mb-6">What We Offer</h3>
            <div class="space-y-6">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900 mb-2">Full Tuition Coverage</h4>
                  <p class="text-gray-600">Complete coverage of university tuition fees for the entire duration of your program.</p>
                </div>
              </div>
              
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg class="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900 mb-2">Living Allowance</h4>
                  <p class="text-gray-600">Monthly stipend to cover accommodation, feeding, and other essential needs.</p>
                </div>
              </div>

              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg class="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900 mb-2">Mentorship Program</h4>
                  <p class="text-gray-600">One-on-one mentorship with industry professionals and successful alumni.</p>
                </div>
              </div>

              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg class="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900 mb-2">Career Development</h4>
                  <p class="text-gray-600">Internship opportunities, skill development workshops, and job placement support.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-2xl shadow-xl p-8">
            <h3 class="text-2xl font-bold text-gray-900 mb-6">Our Impact Story</h3>
            <div class="space-y-4">
              <p class="text-gray-600 leading-relaxed">
                "The Mshel Foundation scholarship didn't just pay for my education; it transformed my entire family's future. Today, I'm a software engineer at a leading tech company, and I'm giving back to other students just like me."
              </p>
              <div class="flex items-center space-x-4">
                <div class="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span class="text-white font-bold">AO</span>
                </div>
                <div>
                  <div class="font-semibold text-gray-900">Adaora Okonkwo</div>
                  <div class="text-sm text-gray-500">Computer Science Graduate, Class of 2022</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Eligibility Criteria -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Eligibility Criteria
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            We're looking for exceptional students who demonstrate academic excellence, leadership potential, and financial need.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Academic Excellence</h3>
            <ul class="space-y-2 text-gray-600">
              <li>• Minimum CGPA of 3.5/4.0 or equivalent</li>
              <li>• Strong JAMB/WAEC results</li>
              <li>• Consistent academic performance</li>
            </ul>
          </div>

          <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border border-purple-100">
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Leadership & Community</h3>
            <ul class="space-y-2 text-gray-600">
              <li>• Demonstrated leadership experience</li>
              <li>• Community service involvement</li>
              <li>• Extracurricular activities</li>
            </ul>
          </div>

          <div class="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-8 border border-green-100">
            <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Financial Need</h3>
            <ul class="space-y-2 text-gray-600">
              <li>• Demonstrated financial need</li>
              <li>• Nigerian citizenship required</li>
              <li>• Admission to accredited university</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Application Process -->
    <section class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Application Process
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our streamlined application process is designed to be simple yet comprehensive. Follow these steps to apply.
          </p>
        </div>

        <div class="relative">
          <!-- Timeline Line -->
          <div class="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-gradient-to-b from-blue-600 to-purple-600 hidden lg:block"></div>
          
          <div class="space-y-12">
            <!-- Step 1 -->
            <div class="relative flex items-center lg:justify-start">
              <div class="flex items-center lg:w-1/2 lg:pr-8 lg:justify-end">
                <div class="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                  <div class="flex items-center mb-4">
                    <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <span class="text-blue-600 font-bold">1</span>
                    </div>
                    <h3 class="text-xl font-bold text-gray-900">Online Application</h3>
                  </div>
                  <p class="text-gray-600">Complete our comprehensive online application form with your personal information, academic records, and essays.</p>
                </div>
              </div>
              <div class="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white hidden lg:block"></div>
            </div>

            <!-- Step 2 -->
            <div class="relative flex items-center lg:justify-end">
              <div class="flex items-center lg:w-1/2 lg:pl-8">
                <div class="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                  <div class="flex items-center mb-4">
                    <div class="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                      <span class="text-purple-600 font-bold">2</span>
                    </div>
                    <h3 class="text-xl font-bold text-gray-900">Document Submission</h3>
                  </div>
                  <p class="text-gray-600">Upload required documents including transcripts, recommendation letters, and financial need documentation.</p>
                </div>
              </div>
              <div class="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-purple-600 rounded-full border-4 border-white hidden lg:block"></div>
            </div>

            <!-- Step 3 -->
            <div class="relative flex items-center lg:justify-start">
              <div class="flex items-center lg:w-1/2 lg:pr-8 lg:justify-end">
                <div class="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                  <div class="flex items-center mb-4">
                    <div class="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                      <span class="text-indigo-600 font-bold">3</span>
                    </div>
                    <h3 class="text-xl font-bold text-gray-900">Review & Interview</h3>
                  </div>
                  <p class="text-gray-600">Our selection committee reviews applications. Shortlisted candidates will be invited for a virtual interview.</p>
                </div>
              </div>
              <div class="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-indigo-600 rounded-full border-4 border-white hidden lg:block"></div>
            </div>

            <!-- Step 4 -->
            <div class="relative flex items-center lg:justify-end">
              <div class="flex items-center lg:w-1/2 lg:pl-8">
                <div class="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                  <div class="flex items-center mb-4">
                    <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <span class="text-green-600 font-bold">4</span>
                    </div>
                    <h3 class="text-xl font-bold text-gray-900">Final Selection</h3>
                  </div>
                  <p class="text-gray-600">Successful candidates receive notification and begin their scholarship journey with orientation and mentorship matching.</p>
                </div>
              </div>
              <div class="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full border-4 border-white hidden lg:block"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Required Documents -->
    <section class="py-20 bg-white">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Required Documents
          </h2>
          <p class="text-xl text-gray-600">
            Ensure you have all the necessary documents ready before starting your application.
          </p>
        </div>

        <div class="bg-gray-50 rounded-2xl p-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Academic Documents</h3>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">Official Academic Transcripts</span>
              </div>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">JAMB Result</span>
              </div>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">WAEC/NECO Certificate</span>
              </div>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">University Admission Letter</span>
              </div>
            </div>

            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Supporting Documents</h3>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">Two Letters of Recommendation</span>
              </div>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">Personal Statement Essay</span>
              </div>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">Financial Need Documentation</span>
              </div>
              <div class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="text-gray-700">Valid ID (National ID/Passport)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Application Timeline -->
    <section class="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <h2 class="text-3xl sm:text-4xl font-bold mb-8">Important Dates</h2>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div class="text-2xl font-bold text-yellow-400 mb-2">March 1</div>
            <div class="text-lg font-semibold mb-2">Application Opens</div>
            <div class="text-sm opacity-90">Begin your scholarship journey</div>
          </div>
          
          <div class="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div class="text-2xl font-bold text-yellow-400 mb-2">June 30</div>
            <div class="text-lg font-semibold mb-2">Application Deadline</div>
            <div class="text-sm opacity-90">Submit all required documents</div>
          </div>
          
          <div class="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div class="text-2xl font-bold text-yellow-400 mb-2">August 15</div>
            <div class="text-lg font-semibold mb-2">Results Announced</div>
            <div class="text-sm opacity-90">Scholarship recipients notified</div>
          </div>
        </div>
      </div>
    </section>
</main>
</template>

<script>
export default {}
</script>

<style scoped></style>